import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0d1218] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <Shield className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white font-['Outfit']">Política de Privacidade</h1>
              <p className="text-slate-400">Última atualização: 26 de Janeiro de 2025</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="glass-card p-8 space-y-8 text-slate-300 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. Introdução e Identificação do Responsável</h2>
            <p>
              A SGFI Capital, Smart Global Financial Insights, S.A., com sede em Portugal, 
              é a entidade responsável pelo tratamento dos seus dados pessoais. A presente Política de 
              Privacidade descreve como recolhemos, utilizamos, armazenamos e protegemos as suas informações 
              pessoais, em conformidade com o Regulamento Geral sobre a Proteção de Dados (RGPD) - 
              Regulamento (UE) 2016/679 do Parlamento Europeu e do Conselho.
            </p>
            <p className="mt-3">
              <strong className="text-white">Contacto do Responsável pelo Tratamento:</strong><br />
              Email: capitalsgfi@gmail.com<br />
              Endereço: Portugal
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Dados Pessoais Recolhidos</h2>
            <p>No âmbito dos nossos serviços, recolhemos as seguintes categorias de dados pessoais:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong className="text-white">Dados de identificação:</strong> Nome completo, número de identificação fiscal (NIF), documento de identificação;</li>
              <li><strong className="text-white">Dados de contacto:</strong> Endereço de email, número de telefone, morada completa;</li>
              <li><strong className="text-white">Dados financeiros:</strong> IBAN, nome do banco, informações de conta Revolut;</li>
              <li><strong className="text-white">Dados de transação:</strong> Histórico de depósitos, levantamentos, rendimentos;</li>
              <li><strong className="text-white">Dados técnicos:</strong> Endereço IP, tipo de navegador, informações de sessão.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. Finalidades do Tratamento</h2>
            <p>Os seus dados pessoais são tratados para as seguintes finalidades:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Gestão da sua conta de investimento e execução do contrato de prestação de serviços;</li>
              <li>Processamento de transações financeiras (depósitos e levantamentos);</li>
              <li>Cumprimento de obrigações legais, incluindo prevenção de branqueamento de capitais e financiamento do terrorismo;</li>
              <li>Verificação de identidade e prevenção de fraude;</li>
              <li>Comunicação de informações relevantes sobre a sua conta;</li>
              <li>Melhoria dos nossos serviços e experiência do utilizador;</li>
              <li>Cumprimento de obrigações fiscais e regulamentares.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. Base Legal para o Tratamento</h2>
            <p>O tratamento dos seus dados pessoais baseia-se nas seguintes bases legais:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong className="text-white">Execução de contrato:</strong> O tratamento é necessário para a execução do contrato de serviços de investimento;</li>
              <li><strong className="text-white">Cumprimento de obrigações legais:</strong> Legislação de prevenção de branqueamento de capitais, obrigações fiscais e regulamentares;</li>
              <li><strong className="text-white">Interesses legítimos:</strong> Prevenção de fraude e segurança dos sistemas;</li>
              <li><strong className="text-white">Consentimento:</strong> Para comunicações de marketing e funcionalidades opcionais.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. Período de Conservação dos Dados</h2>
            <p>
              Os seus dados pessoais são conservados pelo período necessário para as finalidades para que foram 
              recolhidos, nomeadamente:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong className="text-white">Dados contratuais:</strong> Durante a vigência do contrato e pelo período de 10 anos após o seu término;</li>
              <li><strong className="text-white">Dados para cumprimento legal:</strong> Pelo período exigido por lei (mínimo de 7 anos para obrigações fiscais);</li>
              <li><strong className="text-white">Dados de prevenção de branqueamento:</strong> 7 anos após o término da relação comercial;</li>
              <li><strong className="text-white">Dados de sessão e técnicos:</strong> 12 meses.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Partilha de Dados com Terceiros</h2>
            <p>Os seus dados podem ser partilhados com:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong className="text-white">Autoridades reguladoras:</strong> Banco de Portugal, CMVM, Autoridade Tributária, conforme exigido por lei;</li>
              <li><strong className="text-white">Instituições financeiras:</strong> Para processamento de transferências bancárias;</li>
              <li><strong className="text-white">Prestadores de serviços:</strong> Subcontratantes que nos auxiliam na prestação dos serviços, vinculados por acordos de confidencialidade;</li>
              <li><strong className="text-white">Autoridades judiciais:</strong> Em caso de ordem judicial ou investigação legal.</li>
            </ul>
            <p className="mt-3">
              Não vendemos, alugamos ou partilhamos os seus dados pessoais com terceiros para fins de marketing 
              sem o seu consentimento expresso.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">7. Transferências Internacionais de Dados</h2>
            <p>
              Os seus dados pessoais são processados e armazenados em servidores localizados na União Europeia. 
              Caso seja necessária qualquer transferência para fora do Espaço Económico Europeu, garantimos que 
              serão aplicadas as salvaguardas adequadas, incluindo cláusulas contratuais-tipo aprovadas pela 
              Comissão Europeia.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">8. Direitos dos Titulares dos Dados</h2>
            <p>Nos termos do RGPD, tem os seguintes direitos:</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li><strong className="text-white">Direito de acesso:</strong> Obter confirmação sobre se os seus dados são tratados e aceder aos mesmos;</li>
              <li><strong className="text-white">Direito de retificação:</strong> Solicitar a correção de dados inexatos ou incompletos;</li>
              <li><strong className="text-white">Direito ao apagamento:</strong> Solicitar a eliminação dos seus dados, quando aplicável;</li>
              <li><strong className="text-white">Direito à limitação:</strong> Solicitar a limitação do tratamento dos seus dados;</li>
              <li><strong className="text-white">Direito à portabilidade:</strong> Receber os seus dados num formato estruturado e de uso corrente;</li>
              <li><strong className="text-white">Direito de oposição:</strong> Opor-se ao tratamento dos seus dados;</li>
              <li><strong className="text-white">Direito de retirar o consentimento:</strong> Quando o tratamento se basear no consentimento.</li>
            </ul>
            <p className="mt-3">
              Para exercer os seus direitos, contacte-nos através do email: <strong className="text-blue-400">capitalsgfi@gmail.com</strong>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">9. Medidas de Segurança</h2>
            <p>
              Implementamos medidas técnicas e organizacionais adequadas para proteger os seus dados pessoais 
              contra destruição acidental ou ilícita, perda, alteração, divulgação ou acesso não autorizado, 
              incluindo:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              <li>Encriptação de dados em trânsito e em repouso;</li>
              <li>Controlo de acesso baseado em funções;</li>
              <li>Monitorização contínua de segurança;</li>
              <li>Procedimentos de backup e recuperação;</li>
              <li>Formação regular dos colaboradores em proteção de dados.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">10. Reclamações</h2>
            <p>
              Caso considere que o tratamento dos seus dados pessoais viola o RGPD, tem o direito de apresentar 
              uma reclamação junto da Comissão Nacional de Proteção de Dados (CNPD):
            </p>
            <p className="mt-3">
              <strong className="text-white">Comissão Nacional de Proteção de Dados</strong><br />
              Av. D. Carlos I, 134 - 1.º<br />
              1200-651 Lisboa<br />
              Tel: (+351) 21 392 84 00<br />
              Website: www.cnpd.pt
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">11. Alterações à Política de Privacidade</h2>
            <p>
              Reservamo-nos o direito de atualizar esta Política de Privacidade a qualquer momento. Quaisquer 
              alterações serão publicadas nesta página com a data de atualização revista. Recomendamos que 
              consulte periodicamente esta política para se manter informado sobre como protegemos os seus dados.
            </p>
          </section>

          <section className="border-t border-white/10 pt-6">
            <p className="text-sm text-slate-400">
              Esta Política de Privacidade está em vigor desde 26 de Janeiro de 2025 e aplica-se a todos os 
              utilizadores da plataforma SGFI Capital.
            </p>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center text-sm">
          <Link to="/terms" className="text-blue-400 hover:text-blue-300">Termos e Condições</Link>
          <span className="text-slate-600">|</span>
          <Link to="/cookies" className="text-blue-400 hover:text-blue-300">Política de Cookies</Link>
          <span className="text-slate-600">|</span>
          <Link to="/" className="text-blue-400 hover:text-blue-300">Voltar ao Login</Link>
        </div>
      </div>
    </div>
  );
}
